import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useUrqlContext } from '../../../../../context/Urql/UrqlProvider/UrqlContext';
import { AccountMenuPrimaryItem } from '../AccountMenuPrimaryItem';
import { AccountMenuSignOutItemEmbeddedModal } from './components/AccountMenuSignOutItemEmbeddedModal';

// ─────────────────────────────────────────────────────────────────────────────

export const AccountMenuSignOutItem = (props: AccountMenuSignOutProps) => {
  const { isSigningOut, resetNavigation } = props;

  const { resetClient } = useUrqlContext();

  /**
   * Reset GraphQL client and navigation state.
   */
  const resetAppState = useCallback(async () => {
    await resetClient();
    resetNavigation();
  }, [resetClient, resetNavigation]);

  // ─────────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────

  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const openSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(true);
  }, []);

  const closeSignOutModal = useCallback(() => {
    setIsSignOutModalOpen(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <AccountMenuPrimaryItem
        testID="account.sidebar-tab.sign-out"
        label={formatMessage(messages.accountSidebarSignOut)}
        iconName="IconLogout"
        disabled={isSigningOut}
        onPress={openSignOutModal}
      />

      <AccountMenuSignOutItemEmbeddedModal
        isVisible={isSignOutModalOpen}
        closeModal={closeSignOutModal}
        resetAppState={resetAppState}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountSidebarSignOut: {
    defaultMessage: 'Sign out',
    description: 'Account | Sidebar | Sign Out',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountMenuSignOutProps = Readonly<{
  isSigningOut: boolean;
  resetNavigation: () => void;
}>;
